/**
 * useGsapChatOrchestrate - GSAP timeline for /chat empty-state mount sequence.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 2. Per spec F-FE-THEME-GEMINI §6.3 + §6.5.5.
 *
 * Sequence (orchestrated by single gsap.timeline):
 *   1. Sidebar slide-in from left -280px (320ms power3.out) - on mount
 *   2. TopBar fade in opacity 0->1 (240ms)
 *   3. AccentSparkle scaleIn 0.92->1 (480ms power3.out)
 *   4. Greeting headline scaleIn 0.92->1 (480ms power3.out, 80ms after sparkle)
 *   5. SuggestionChip stagger 60ms reveal (320ms power2.out)
 *   6. PillInput fade + slideUp 16px (320ms)
 *
 * Reduced-motion: skip entirely, render instant per AC-14.
 *
 * Lazy-loaded GSAP chunk (already split via vite.config.ts manualChunks).
 *
 * Selectors are data-attribute based:
 *   [data-nav-rail]       -> NavRail app shell (spec 2026-06-12)
 *   [data-chat-sidebar]   -> sidebar element
 *   [data-chat-topbar]    -> topbar element
 *   [data-greeting-sparkle] -> sparkle wrap inside GreetingBlock
 *   [data-greeting-headline] -> h1 inside GreetingBlock
 *   [data-suggestion-chip] -> each chip
 *   [data-pill-input]     -> pill input wrap
 *
 * Usage: call once on /chat mount with the chat shell ref. Hook returns void.
 * Cleanup via ctx.revert() on unmount.
 */

import { useEffect } from 'react';
import type { RefObject } from 'react';

export type ChatOrchestrateOptions = {
  /** When true, skip orchestration (vd messages.length > 0 - chat already populated). */
  skip?: boolean;
};

export function useGsapChatOrchestrate(
  shellRef: RefObject<HTMLElement | null>,
  options: ChatOrchestrateOptions = {}
) {
  const { skip = false } = options;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (skip) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let cleanup: (() => void) | undefined;

    (async () => {
      try {
        const { gsap } = await import('gsap');
        const ctx = gsap.context(() => {
          const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

          // 0. NavRail slide-in (app shell, spec 2026-06-12)
          tl.from('[data-nav-rail]', {
            x: -72,
            opacity: 0,
            duration: 0.28,
          }, 0);

          // 1. Sidebar slide-in
          tl.from('[data-chat-sidebar]', {
            x: -280,
            opacity: 0,
            duration: 0.32,
          }, 0);

          // 2. TopBar fade
          tl.from('[data-chat-topbar]', {
            opacity: 0,
            duration: 0.24,
          }, 0.04);

          // 3. Sparkle scaleIn
          tl.from('[data-greeting-sparkle]', {
            opacity: 0,
            scale: 0.92,
            transformOrigin: 'center center',
            duration: 0.48,
          }, 0.16);

          // 4. Headline scaleIn (80ms after sparkle)
          tl.from('[data-greeting-headline]', {
            opacity: 0,
            scale: 0.96,
            y: 8,
            transformOrigin: 'center center',
            duration: 0.48,
          }, 0.24);

          // 5. Suggestion chip stagger after greeting
          tl.from('[data-suggestion-chip]', {
            opacity: 0,
            y: 12,
            duration: 0.32,
            ease: 'power2.out',
            stagger: 0.06,
          }, 0.56);

          // 6. PillInput fade + slideUp
          tl.from('[data-pill-input]', {
            opacity: 0,
            y: 16,
            duration: 0.32,
            ease: 'power2.out',
          }, 0.4);
        }, shellRef.current ?? undefined);

        cleanup = () => ctx.revert();
      } catch (e) {
        if (typeof console !== 'undefined') {
          // eslint-disable-next-line no-console
          console.warn('[useGsapChatOrchestrate] GSAP load failed:', e);
        }
      }
    })();

    return () => {
      cleanup?.();
    };
  }, [shellRef, skip]);
}

export default useGsapChatOrchestrate;
